import {
  IRawSubscription,
  ISubscription,
  TOldPrices,
  TRawOldPrices,
} from 'models/subscriptionsApi'

import { CENTS_IN_DOLLAR, INITIAL_PRICE } from 'root-constants'

const getFormattedPrice = (price: number | undefined): number =>
  price ? price / CENTS_IN_DOLLAR : INITIAL_PRICE

const getCurrentOldPrices = (oldPrices: TRawOldPrices): TOldPrices => {
  const prices =
    oldPrices.before_custom_discount || oldPrices.before_50_percent_discount

  return {
    fullPrice: getFormattedPrice(prices.amount),
    weekly: getFormattedPrice(prices.in_the_box.weekly),
    daily: getFormattedPrice(prices.in_the_box.daily),
    percentOfDiscount: prices.percent_of_discount,
  }
}

export const getSubscriptionListFromRawSubscriptionList = (
  rawSubscriptionList: IRawSubscription,
): ISubscription[] =>
  rawSubscriptionList.plans.map((rawSubscription) => ({
    id: rawSubscription.paywall.show_order.toString(),
    planId: rawSubscription.id,
    product: rawSubscription.product_key,
    type: rawSubscription.type,
    paypalPlanId: rawSubscription.paypal?.plan_id,
    isDefault: rawSubscription.paywall.is_default,
    currency: rawSubscription.pricing.currency.toLowerCase(),
    lookupKey: rawSubscription.stripe.main_lookup_key,
    trialLookupKey: rawSubscription.stripe.intro_lookup_key,

    mainPrices: {
      periodName: rawSubscription.pricing.recurring?.interval,
      periodQuantity: rawSubscription.pricing.recurring?.interval_count,
      fullPrice: getFormattedPrice(rawSubscription.main.current_price.amount),
      fullPriceTax: getFormattedPrice(
        rawSubscription.main.current_price.tax_amount,
      ),
      daily:
        rawSubscription.main.current_price.in_the_box.daily / CENTS_IN_DOLLAR,
      weekly:
        rawSubscription.main.current_price.in_the_box.weekly / CENTS_IN_DOLLAR,
      monthly:
        rawSubscription.main.current_price.in_the_box.monthly / CENTS_IN_DOLLAR,
      yearly:
        rawSubscription.main.current_price.in_the_box.yearly / CENTS_IN_DOLLAR,
      discountPercentage:
        rawSubscription.main.old_prices.before_custom_discount
          ?.percent_of_discount,
      discountAmount:
        (rawSubscription.main.old_prices.before_custom_discount
          ?.amount_of_discount || 0) / CENTS_IN_DOLLAR,
      oldPrices: getCurrentOldPrices(rawSubscription.main.old_prices),
    },

    introDiff: {
      discountAmount: getFormattedPrice(
        rawSubscription.intro_diff?.discount_amount,
      ),
      discountPercentage: rawSubscription.intro_diff?.discount_percent,
    },

    trialPrices: {
      fullPrice: getFormattedPrice(rawSubscription.trial?.current_price.amount),
      fullPriceTax: getFormattedPrice(
        rawSubscription.trial?.current_price.tax_amount,
      ),
      durationDays: rawSubscription.pricing.trial?.duration_days,
      daily: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.daily,
      ),
      weekly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.weekly,
      ),
      monthly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.monthly,
      ),
      yearly: getFormattedPrice(
        rawSubscription.trial?.current_price.in_the_box.yearly,
      ),
      discountPercentage:
        rawSubscription.trial?.old_prices.before_custom_discount
          ?.percent_of_discount,
      oldPrices:
        rawSubscription.trial &&
        getCurrentOldPrices(rawSubscription.trial.old_prices),
    },
    upgradeDiff: {
      amountToPay: rawSubscription.upgrade_diff?.amount_to_pay,
      amountPaid: rawSubscription.upgrade_diff?.trial_price,
      oldFullPrice: rawSubscription.upgrade_diff?.old_full_price,
      fullPrice: rawSubscription.upgrade_diff?.full_price,
    },
  }))
